import { useEffect, useState } from 'react'
import { Box, Stack, Typography } from '@mui/material'

import { INTERAX_COLORS } from '../../../../../constants/colors'
import { TESTIDS } from '../../../../../config/testids'
import { useTranslation } from '../../../../../shared/hooks/useIntractionTranslate'
import { DefaultModal } from '../../../../../shared/components'
import { Spinner } from '../../../../../interaxDesignSystemComponents'
import { useFeedbackReadiness, useLatestFeedbackDataForGamer } from '../../../../../apiLayer/queryHooks/queries'
import { isValidFeedbackData } from '../../helpers/latestSessionFeedbackHelpers'
import { useLatestSessionFeedbackView } from '../../hooks/useLatestSessionFeedbackView'
import { FEEDBACK_ATTENTION_MS, coachFeedbackAttentionSx } from '../coachPageStyles'
import { CoachSessionFeedbackSections } from './CoachSessionFeedbackSections'

type CoachSessionFeedbackModalProps = {
  isOpen: boolean
  onClose: () => void
}

/**
 * The latest session feedback beside the conversation, since leaving the page ends a spoken session. Reads
 * the same data through the same hooks the full page uses, so the two can never describe a session differently.
 */
export const CoachSessionFeedbackModal = ({ isOpen, onClose }: CoachSessionFeedbackModalProps) => {
  const { t } = useTranslation()

  // Only fetched once it has been asked for: the coach page must not pay for a report nobody opened.
  const { data: feedbackResponse, isLoading, error } = useLatestFeedbackDataForGamer({ enabled: isOpen })
  const hasApiError = Boolean(error) || feedbackResponse?.success === false || Boolean(feedbackResponse?.error)
  const readiness = useFeedbackReadiness({ enabled: isOpen && !hasApiError })
  const isReady = readiness?.data?.data?.isReady ?? false

  const feedbackData = feedbackResponse?.data
  const view = useLatestSessionFeedbackView(feedbackData)

  // A brief lift on open, since this appears over a conversation someone is in the middle of.
  const [isDrawingAttention, setIsDrawingAttention] = useState(false)
  useEffect(() => {
    if (!isOpen) return
    setIsDrawingAttention(true)
    const timer = window.setTimeout(() => setIsDrawingAttention(false), FEEDBACK_ATTENTION_MS)
    return () => window.clearTimeout(timer)
  }, [isOpen])

  const hasContent = isReady && isValidFeedbackData(feedbackData)

  return (
    <DefaultModal isOpen={isOpen} onClose={onClose} maxWidth="900px">
      <Stack
        spacing={2}
        data-testid={TESTIDS.COACH_FEEDBACK_MODAL}
        sx={{ maxHeight: '78vh', overflowY: 'auto', pr: { xs: 0.5, sm: 1, md: 1 } }}
      >
        <Typography sx={{ color: INTERAX_COLORS.white, fontWeight: 700, fontSize: 20 }}>
          {t('gamer.ariaCoach.feedbackModalTitle')}
        </Typography>

        {isLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <Spinner size="large" color="sun" />
          </Box>
        )}

        {!isLoading && !hasContent && (
          <Typography sx={{ color: INTERAX_COLORS.thunderGrey, fontSize: 14 }}>
            {hasApiError ? t('gamer.ariaCoach.feedbackModalError') : t('gamer.ariaCoach.feedbackModalPending')}
          </Typography>
        )}

        {hasContent && feedbackData && (
          <Box sx={isDrawingAttention ? coachFeedbackAttentionSx : undefined}>
            <CoachSessionFeedbackSections feedbackData={feedbackData} view={view} />
          </Box>
        )}
      </Stack>
    </DefaultModal>
  )
}
